import { useEffect } from 'react';

import { useAuthStore } from '@/stores/auth-store';

/**
 * AuthBootstrap — single source of truth for the Supabase auth listener
 * subscription (F12.1 Path 2 / NC2).
 *
 * Mounted ONCE above all routes in <App /> so that public routes which sit
 * outside <AuthGuard /> (`/invite/:token`, `/shared/:token`, `/auth/callback`)
 * still see Zustand `status` resolve from `'loading'` → `'authenticated'` |
 * `'unauthenticated'`. Pre-F12.1, the only call to `initialize()` lived inside
 * AuthGuard's mount effect, which meant a user navigating directly to
 * `/invite/<token>` while authenticated would see the page hang in
 * `status: 'loading'` forever and the public sign-in CTA would leak.
 *
 * NC3 constraint — this component subscribes the auth listener ONLY. It must
 * never call `syncService.start`, `loadActiveProperty`, `notificationService.init`,
 * or any other AppShell-scoped side-effect. Those side-effects belong to the
 * authenticated app shell and only run after `<AuthGuard>` lets the
 * authenticated subtree render. A grep audit at the top of `src/App.tsx`
 * documents the no-AppShell-side-effects invariant for this file.
 */
export function AuthBootstrap(): null {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
    // initialize is a stable Zustand action reference — the [] dep array is
    // intentional. Pinned by the AuthBootstrap.test.tsx "calls initialize once
    // on mount" test which asserts toHaveBeenCalledTimes(1) across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
